import React, { useState } from "react";
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import type { BenchmarkId, ParsedProfile } from "../types";
import s from "./ProfileStep.module.scss";
import { parseProfile, parseProfileText, runAssessment } from "../api";
import type { RunResponse } from "../types";

interface ProfileStepProps {
  onStart: (workflowId: string, questions: RunResponse["questions"]) => void;
  onError: (error: string) => void;
}

const BENCHMARKS: Array<{ id: BenchmarkId; label: string }> = [
  { id: "hcbp", label: "HC Business Partner" },
  { id: "hcd", label: "HC Director" },
  { id: "hr_manager", label: "HR Manager" },
  { id: "recruiter", label: "Recruiter" },
  { id: "payroll", label: "Payroll Specialist" },
  { id: "trainer", label: "Trainer" },
  { id: "l_and_d_manager", label: "Learning & Development Manager" },
];

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".pdf", ".md", ".markdown", ".txt", ".json"];

type InputMode = "upload" | "paste";

export default function ProfileStep({
  onStart,
  onError,
}: ProfileStepProps): JSX.Element {
  const [inputMode, setInputMode] = useState<InputMode>("upload");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState("");
  const [benchmarkId, setBenchmarkId] = useState<BenchmarkId>("hcbp");
  const [jobLevel, setJobLevel] = useState<number>(3);
  const [parseError, setParseError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleModeChange = (mode: InputMode) => {
    if (mode === inputMode) return;
    setInputMode(mode);
    setUploadedFile(null);
    setPastedText("");
    setParseError("");
  };

  const handleBenchmarkChange = (event: SelectChangeEvent<BenchmarkId>) => {
    setBenchmarkId(event.target.value as BenchmarkId);
  };

  const handleJobLevelChange = (event: SelectChangeEvent<number>) => {
    setJobLevel(Number(event.target.value));
  };

  const validateFile = (file: File): string | null => {
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Unsupported format. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `File too large. Max ${MAX_FILE_SIZE_MB} MB.`;
    }
    return null;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { setParseError(err); return; }
    setUploadedFile(file);
    setParseError("");
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { setParseError(err); return; }
    setUploadedFile(file);
    setParseError("");
  };

  const handleLaunchAssessment = async () => {
    setParseError("");
    setLoading(true);
    onError("");

    try {
      let profile: ParsedProfile;

      if (inputMode === "upload" && uploadedFile) {
        profile = await parseProfile(uploadedFile);
      } else if (inputMode === "paste" && pastedText.trim()) {
        profile = await parseProfileText(pastedText, "pasted_profile.txt");
      } else {
        setParseError("Please provide a file or pasted text to continue.");
        setLoading(false);
        return;
      }

      const response = await runAssessment({ ...profile, job_level: jobLevel }, benchmarkId);
      onStart(response.workflow_id, response.questions);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to launch assessment. Please try again.";
      onError(msg);
    } finally {
      setLoading(false);
    }
  };

  const canLaunch =
    !loading &&
    ((inputMode === "upload" && !!uploadedFile) ||
      (inputMode === "paste" && pastedText.trim().length > 0));

  return (
    <div className={s.container}>
      {parseError && (
        <Alert severity="error" onClose={() => setParseError("")}>
          {parseError}
        </Alert>
      )}

      {/* Hero */}
      <div className={s.heroSection}>
        <h2 className={s.heroTitle}>Upload your profile to get started</h2>
        <p className={s.heroSubtitle}>
          We'll analyse your skills against the selected role benchmark and generate
          a personalised set of assessment questions.
        </p>
      </div>

      {/* Tab bar */}
      <div className={s.tabBar}>
        <button
          type="button"
          className={`${s.tabBtn} ${inputMode === "upload" ? s.tabBtnActive : ""}`}
          onClick={() => handleModeChange("upload")}
          disabled={loading}
        >
          Upload File
        </button>
        <button
          type="button"
          className={`${s.tabBtn} ${inputMode === "paste" ? s.tabBtnActive : ""}`}
          onClick={() => handleModeChange("paste")}
          disabled={loading}
        >
          Paste Text
        </button>
      </div>

      {/* Input panel — two columns */}
      <div className={s.inputPanel}>
        {/* Left: upload zone or paste area */}
        {inputMode === "upload" ? (
          <div
            className={`${s.uploadZone} ${dragOver ? s.uploadZoneHover : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <CloudUploadIcon className={s.uploadIcon} />
            <p className={s.uploadTitle}>
              {dragOver ? "Drop it here" : "Drag & drop your profile"}
            </p>
            <p className={s.uploadHint}>
              .pdf — max {MAX_FILE_SIZE_MB} MB
            </p>

            <input
              accept={ALLOWED_EXTENSIONS.join(",")}
              className={s.hiddenInput}
              id="file-upload"
              type="file"
              onChange={handleFileChange}
              disabled={loading}
            />
            <label htmlFor="file-upload">
              <button
                type="button"
                className={s.chooseFileBtn}
                disabled={loading}
                onClick={() => document.getElementById("file-upload")?.click()}
              >
                Choose File
              </button>
            </label>

            {uploadedFile && (
              <span className={s.uploadedBadge}>
                <CheckCircleIcon style={{ fontSize: 15 }} />
                {uploadedFile.name}
              </span>
            )}
          </div>
        ) : (
          <div className={s.pasteWrapper}>
            <TextField
              label="Paste profile text"
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              multiline
              rows={9}
              fullWidth
              disabled={loading}
              placeholder="Paste your resume or profile text here…"
              helperText="Supports plain text, Markdown, or JSON"
              variant="outlined"
            />
          </div>
        )}

        {/* Right: info card */}
        <div className={s.infoCard}>
          <p className={s.infoCardTitle}>Skills Assessed</p>
          <ul className={s.infoSkillList}>
            <li className={s.infoSkillItem}>
              <span className={s.infoSkillDot} data-type="technical" />
              Technical
            </li>
            <li className={s.infoSkillItem}>
              <span className={s.infoSkillDot} data-type="operational" />
              Operational
            </li>
            <li className={s.infoSkillItem}>
              <span className={s.infoSkillDot} data-type="behavioural" />
              Behavioural
            </li>
          </ul>
          <p className={s.infoNote}>
            Your profile is parsed and matched against the benchmark for your
            chosen role. Questions are tailored to the gaps we find.
          </p>
        </div>
      </div>

      {/* Config row — role + level */}
      <div className={s.configRow}>
        <FormControl fullWidth disabled={loading}>
          <InputLabel id="benchmark-label">Role</InputLabel>
          <Select
            labelId="benchmark-label"
            value={benchmarkId}
            label="Role"
            onChange={handleBenchmarkChange}
          >
            {BENCHMARKS.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={loading}>
          <InputLabel id="job-level-label">Job Level</InputLabel>
          <Select
            labelId="job-level-label"
            value={jobLevel}
            label="Job Level"
            onChange={handleJobLevelChange}
          >
            {[1, 2, 3, 4, 5].map((level) => (
              <MenuItem key={level} value={level}>
                Level {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Footer — button pinned to the right */}
      <div className={s.footer}>
        <span className={s.coverText}>
          Assessment covers <strong>Technical</strong>,{" "}
          <strong>Operational</strong> and <strong>Behavioural</strong> skills.
        </span>
        <button
          type="button"
          className={s.launchBtn}
          onClick={handleLaunchAssessment}
          disabled={!canLaunch}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <RocketLaunchIcon className={s.launchBtnIcon} />
          )}
          {loading ? "Launching…" : "Launch Assessment"}
        </button>
      </div>
    </div>
  );
}
